import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from '@components/avatar';
import { Checkbox } from '@components/ui/checkbox';
import { Form, FormField } from '@components/ui/form';
import { FormItemWrapper } from '@components/ui/form/itemWrapper';
import { Input } from '@components/ui/form/input';
import { MultiSelect } from '@components/ui/form/multiSelect';
import { Loader } from '@components/loader';
import { SchedulerDate } from '@components/scheduler/date';
import { SchedulerSidebar } from '@components/scheduler/sidebar';
import { SchedulerShell } from '@components/scheduling/shell';
import { SchedulerBlock } from '@components/scheduling/block';
import { PersonCard } from '@components/scheduling/personCard';
import { TimeRangePicker } from '@components/scheduling/timeRangePicker';
import { TimeRange } from '@components/scheduling/timeRangePicker/types';
import { useInternalScheduler } from '@hooks/scheduler/internal';
import { useSchedulerSync } from '@hooks/scheduler/sync';
import { useAssignForeman } from '@hooks/scheduler/assign';
import { formatTime, shortDate, shortDay, shortDayDate } from '@utils/i18n';
import { toTenantDate } from '@utils/timeZone';
import { getShift } from '@utils/schedule/tenantShift';
import { InternalScheduleDialogProps } from '../types';
import { InternalScheduleActions } from '../actions';
import { OverlapScheduleDialog } from '../action/overlap';
import { SpecialtyOverrideDialog } from '../action/specialty';
import { SeriesCalendarLegend } from '../seriesCalendar';
import { seriesDayCalendarStyle } from '../seriesCalendar/constants';
import { hasConflict, toForeman } from './helpers';

export default function MobileInternalScheduleDialog({
  open,
  setOpen,
  type,
  schedule,
  jobId,
  initialDate,
  activeId,
  readOnly,
  initialParams = {},
}: InternalScheduleDialogProps) {
  const { t } = useTranslation('app');
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pinnedCalendarOpen, setPinnedCalendarOpen] = useState(false);
  const [draftTime, setDraftTime] = useState<TimeRange>(getShift);
  const assignForeman = useAssignForeman();
  const {
    form,
    onSubmit,
    locked,
    data,
    schedulesFetching,
    schedulesLoading,
    overlap,
    setOverlap,
    conflicts,
    specialtyOverride,
    setSpecialtyOverride,
    selectedSpecialties,
    setSelectedSpecialties,
    selectedForemen,
    setSelectedForemen,
    allUsers,
    setParam,
    specialties,
    fieldUsers,
    rawDate,
    setRawDate,
    active,
    jobSubtitle,
    multiDayPosition,
    seriesRange,
    calendarModifiers,
    showSeriesCalendar,
    timeRange,
    setActiveTime,
    calendarOpen,
    setCalendarOpen,
    scheduler,
  } = useInternalScheduler({
    open,
    setOpen,
    type,
    schedule,
    jobId,
    initialDate,
    readOnly,
    initialParams,
  });
  const { setValue } = form;

  useSchedulerSync({
    date: rawDate,
    foremen: data?.records,
    withoutTeam: data?.withoutTeam,
    outOfScopeRecords: data?.outOfScopeRecords,
    modal: true,
    schedule,
    activeId,
  });

  const activeSchedule = active.schedule.id ? active.schedule : undefined;
  const activeForemanId = activeSchedule?.foreman?.id || '';
  const disabled = Boolean(readOnly);
  const assign = (foreman?: Parameters<typeof assignForeman>[0]) =>
    assignForeman(foreman, { day: scheduler.date || rawDate, time: draftTime });
  const timeText = activeSchedule
    ? [activeSchedule.startsAt, activeSchedule.endsAt]
        .map((value) => formatTime(toTenantDate(value)))
        .join(' - ')
    : '';
  const others = (list: (typeof activeSchedule)[] = []) =>
    (list.filter(Boolean) as NonNullable<typeof activeSchedule>[]).filter(
      (item) => item.id !== activeSchedule?.id,
    );

  const renderWhen = (idPrefix: string) => {
    const pinned = idPrefix !== 'scheduler';
    return (
      <div className="flex flex-col items-stretch gap-2.5">
        <SchedulerDate
          className="w-full"
          date={rawDate}
          setDate={setRawDate}
          loading={Boolean(schedulesFetching)}
          calendar={{
            open: pinned ? pinnedCalendarOpen : calendarOpen,
            setOpen: pinned ? setPinnedCalendarOpen : setCalendarOpen,
            ...(showSeriesCalendar && {
              modifiers: calendarModifiers,
              modifiersClassNames: seriesDayCalendarStyle,
              legend: <SeriesCalendarLegend modifiers={calendarModifiers} />,
            }),
          }}
        />
        {multiDayPosition && (
          <p className="-mt-1.5 text-center text-xs text-neutral-500">
            {[
              t('schedule.multiDay.dayOf', multiDayPosition),
              seriesRange && [...new Set(seriesRange.map(shortDate))].join(' – '),
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        )}
        <TimeRangePicker
          id={`${idPrefix}-time`}
          value={timeRange || draftTime}
          onChange={activeSchedule ? setActiveTime : setDraftTime}
          disabled={disabled}
        />
      </div>
    );
  };

  return (
    <>
      <Form {...form}>
        <SchedulerShell
          open={open}
          onOpenChange={setOpen}
          title={t('schedule.team', { team: type?.name })}
          subtitle={jobSubtitle}
          popoverRef={popoverRef}
          renderWhen={renderWhen}
          summary={
            <>
              <b>
                {shortDay(rawDate)}, {shortDate(rawDate)}
              </b>
              {timeText && <span>· {timeText}</span>}
            </>
          }
          footerExtra={
            !readOnly && (
              <FormField
                control={form.control}
                name="confirmed"
                render={({ field: { value, onChange } }) => (
                  <Checkbox
                    id="mobile-confirmed"
                    className="h-6 w-6"
                    checked={Boolean(value)}
                    onCheckedChange={onChange}>
                    {t('schedules.markAsConfirmed')}
                  </Checkbox>
                )}
              />
            )
          }
          footer={
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            <form onSubmit={form.handleSubmit(onSubmit)}>
              {(['forceOverlapping', 'foremanId', 'startsAt', 'endsAt'] as const).map((name) => (
                <FormField
                  key={name}
                  control={form.control}
                  name={name}
                  render={({ field }) => (
                    <input type="hidden" {...field} value={String(field.value ?? '')} />
                  )}
                />
              ))}
              <div className="[&>div>div:last-child]:grid [&>div>div:last-child]:auto-cols-fr [&>div>div:last-child]:grid-flow-col [&>div>div:last-child]:gap-2 [&_button]:h-11 [&_button]:w-full [&_button]:justify-center">
                <InternalScheduleActions
                  hideCancel
                  onCancel={() => setOpen(false)}
                  onSendSchedule={() => {
                    setValue('actionType', 'saveAndSend');
                    void form.handleSubmit(onSubmit)();
                  }}
                  onRescheduleAsDraft={() => {
                    setValue('status', 'draft');
                    setValue('actionType', '');
                    void form.handleSubmit(onSubmit)();
                  }}
                  onSaveAsDraft={() => setValue('actionType', '')}
                  locked={locked || !active.schedule.id}
                  scheduleId={schedule?.id}
                  foremanId={active.schedule.foreman?.id}
                  readOnly={readOnly}
                  isDraft={schedule?.status === 'draft'}
                />
              </div>
            </form>
          }>
          <SchedulerBlock title={t('scheduling.assignTo')}>
            {!readOnly && (
              <div className="mb-3 flex flex-col gap-2">
                <span className="text-[13px] font-semibold text-neutral-500">
                  {t('scheduling.specialty')}
                </span>
                <div className="[&>button]:w-full [&_button[aria-haspopup]]:w-full">
                  <MultiSelect
                    placeholder={t('filters.teams.placeholder')}
                    className="w-full"
                    options={specialties.options.map((el) => ({ value: el.value, label: el.name }))}
                    value={selectedSpecialties}
                    limit={1}
                    onChange={setSelectedSpecialties}
                    disabled={specialties.disabled}
                    side="bottom"
                    popoverRef={popoverRef}
                  />
                </div>
                <span className="text-[13px] font-semibold text-neutral-500">
                  {t('scheduling.fieldUsers')}
                </span>
                <div className="[&>button]:w-full [&_button[aria-haspopup]]:w-full">
                  <MultiSelect
                    placeholder={t('filters.foremen.placeholder')}
                    className="w-full"
                    options={fieldUsers.options.map((el) => ({
                      value: el.id,
                      label: el.fullName || '',
                    }))}
                    value={selectedForemen}
                    limit={1}
                    onChange={setSelectedForemen}
                    disabled={fieldUsers.disabled}
                    side="bottom"
                    popoverRef={popoverRef}
                  />
                </div>
                <Checkbox
                  id="mobile-all-divisions"
                  className="h-6 w-6"
                  checked={allUsers === 'true'}
                  onCheckedChange={() => setParam('allUsers', allUsers === 'true' ? '' : 'true')}>
                  {t('schedules.allDivisions')}
                </Checkbox>
              </div>
            )}
            <div
              role="radiogroup"
              aria-label={t('scheduling.assignTo')}
              className="relative flex min-h-24 flex-col gap-2"
              aria-busy={schedulesLoading}>
              {schedulesLoading ? (
                <Loader local />
              ) : (
                <>
                  <PersonCard
                    name={t('scheduling.withoutTeam')}
                    avatar={<Avatar hideTooltip name="" className="h-9 w-9 bg-neutral-100" />}
                    selected={Boolean(activeSchedule) && !activeForemanId}
                    disabled={disabled}
                    schedules={others(data?.withoutTeam)}
                    currentSchedule={activeSchedule}
                    onSelect={() => assign(undefined)}
                  />
                  {(data?.records || []).map((record) => {
                    const selected = activeForemanId === record.id;
                    const schedules = others(record.schedules);
                    return (
                      <PersonCard
                        key={record.id}
                        name={record.fullName}
                        subtitle={record.specialties.map((el) => el.name).join(', ')}
                        avatar={<Avatar hideTooltip name={record.fullName} className="h-9 w-9" />}
                        selected={selected}
                        conflict={selected && hasConflict(activeSchedule, schedules)}
                        disabled={disabled || Boolean(record.discardedAt)}
                        schedules={schedules}
                        currentSchedule={activeSchedule}
                        onSelect={() => assign(toForeman(record))}
                      />
                    );
                  })}
                </>
              )}
            </div>
          </SchedulerBlock>
          <SchedulerBlock className="px-0 py-0">
            <SchedulerSidebar />
          </SchedulerBlock>
          <SchedulerBlock>
            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItemWrapper label={t('forms.note.label')}>
                  <Input disabled={readOnly} wrapperClassName="h-11" {...field} />
                </FormItemWrapper>
              )}
            />
          </SchedulerBlock>
        </SchedulerShell>
        {overlap && (
          <OverlapScheduleDialog
            sheet
            open={overlap}
            setOpen={setOverlap}
            disabled={locked || !active.schedule.id}
            onCancel={() => setValue('forceOverlapping', false)}
            onConfirm={() => {
              setValue('forceOverlapping', true);
              void form.handleSubmit(onSubmit)();
            }}
            conflicts={conflicts || []}
            date={
              active.schedule?.startsAt ? shortDayDate(toTenantDate(active.schedule.startsAt)) : ''
            }
            name={active.schedule?.task?.name}
            summaryDetail={
              active.schedule?.startsAt
                ? `${timeText}, ${shortDayDate(toTenantDate(active.schedule.startsAt))}`
                : ''
            }
          />
        )}
        <SpecialtyOverrideDialog
          sheet
          open={specialtyOverride}
          setOpen={setSpecialtyOverride}
          disabled={locked || !active.schedule.id}
          onCancel={() => setValue('forceSpecialty', false)}
          onConfirm={() => {
            setValue('forceSpecialty', true);
            void form.handleSubmit(onSubmit)();
          }}
        />
      </Form>
    </>
  );
}
